import { api } from './config';
import { ApiResponse } from '../types';
import { fallbackCountries, getSortedFallbackCountries, FallbackCountry } from '../data/countries-fallback';

export interface Country {
  code: string;
  name_en: string;
  name_kr: string;
  continent: string;
}

export const countriesApi = {
  /**
   * Get all active countries from the database
   * Falls back to local data if the API request fails
   */
  getAll: async (): Promise<{ data: ApiResponse<Country[]> }> => {
    try {
      const response = await api.get<ApiResponse<Country[]>>('/countries');
      return response;
    } catch (error) {
      console.warn('Failed to fetch countries from database, using fallback data:', error);
      // Return fallback data in the same format as the API response
      return {
        data: {
          success: true,
          data: getSortedFallbackCountries() as Country[],
          message: 'Using fallback country data',
        },
      };
    }
  },

  /**
   * Get country by code
   * Falls back to local data if the API request fails
   */
  getByCode: async (code: string): Promise<{ data: ApiResponse<Country> }> => {
    try {
      const response = await api.get<ApiResponse<Country>>(`/countries/${code}`);
      return response;
    } catch (error) {
      console.warn(`Failed to fetch country ${code} from database, using fallback data:`, error);
      const country = fallbackCountries.find((c) => c.code.toLowerCase() === code.toLowerCase());
      if (country) {
        return {
          data: {
            success: true,
            data: country as Country,
            message: 'Using fallback country data',
          },
        };
      }
      throw error; // Re-throw if country not found in fallback
    }
  },

  /**
   * Get fallback countries directly (for offline usage or when API is known to be unavailable)
   */
  getFallbackCountries: (): Country[] => {
    return getSortedFallbackCountries() as Country[];
  },
};
